/**
 * The socket and the snapshot buffer -- everything that touches the wire,
 * and nothing that touches the scene.
 *
 * Shaped after BreachProtocol's Connection: deliberately dumb.  It does not
 * know what a cat looks like; it parses, buffers, and hands out samples.  The
 * facade in index.js subscribes by message tag and gets the parsed payload,
 * which keeps the message wiring there readable as a list of rules.
 *
 * The one hard rule inherited from that codebase: NOTHING in here may throw.
 * onmessage runs on the same thread as requestAnimationFrame, and an
 * exception in a handler takes the render loop with it -- a malformed frame
 * must cost one dropped message, never a black screen.  So every inbound
 * byte goes through parseServerMessage, every row through _ingestSnap's own
 * checks, and every subscriber call through a try/catch.
 */
import { InterpDelay } from "./interp.js";
import { parseServerMessage, poseFrame, CatCol, CAT_ROW_LEN } from "@catworld/shared";
import { CLOSE, MAX_CATS, STATES, STATE_SEND_MS } from "@catworld/shared";

/** Synthetic tags, so the facade can react to the socket's own lifecycle
    through the same handler table as real messages.  Underscored: no server
    message tag will ever collide with them (protocol.js owns that namespace
    and its tags are words). */
export const OPENED = "_open";
export const CLOSED = "_close";

/** App-level round-trip probe.  ~2 s: fast enough that the HUD figure means
    "now", slow enough to be free -- one ping is smaller than half a pose. */
const PING_MS = 2000;

/**
 * Reconnect backoff: 1 s doubling to 15 s, each wait jittered to 70-130%.
 * The jitter is not decoration -- all sixteen clients lose the same server at
 * the same moment when it redeploys, and without it they all knock again in
 * the same instant, every cycle, forever.  15 s cap because past that the
 * player has read the "offline" HUD line and a faster retry costs nothing.
 */
const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 15000;

/**
 * How many times to knock on a door nobody has EVER answered.  A LAN static
 * server or a half-configured deploy simply has no /ws, and it will not grow
 * one -- endless retries there are endless red lines in the console of a game
 * that is otherwise playing perfectly well single-player.  A server that has
 * answered once and vanished is different: that is a redeploy, and those we
 * chase forever (at the capped backoff).
 */
const NEVER_CONNECTED_TRIES = 3;

/** Seconds of snapshot history kept.  The render clock sits at most
    INTERP_MAX (0.30 s) behind the newest snapshot, so 3 s is ten times the
    deepest legitimate read -- the slack is for debugging with the buffer
    visible, not for the interpolator. */
const SNAP_KEEP_S = 3;
/** Hard cap on entries, in case a misbehaving server stamps faster than it
    ticks: 96 rows is the 3 s window at 32 Hz.  Memory guard, not policy. */
const SNAP_MAX = 96;

/**
 * Easing constant for the render clock, seconds.  The clock advances by dt
 * and then eases the remaining error toward `newest - delay`.  A hard
 * assignment every frame would quantise all motion to the snapshot grid and
 * undo the interpolation entirely -- BreachProtocol's comment, and it is
 * right: the target only moves when a snapshot lands, so assigning it makes
 * the render clock a 20 Hz staircase.  0.15 s closes half the error in
 * ~100 ms, tight enough that the delay adapts within a couple of snapshots
 * and loose enough that the staircase never shows.
 */
const RENDER_EASE_TAU = 0.15;

/** Past this much render-clock error, ease is the wrong tool: a server
    restart moved the whole timeline, and swimming a second of it would
    fast-forward every cat across the garden.  Snap instead. */
const RENDER_SNAP_S = 1.0;

/** Wrap b - a into (-PI, PI] so a facing lerp always turns the short way.
    This is the classic bug: a cat oscillating around +/-PI (facing -Z, which
    is toward the houses, so it is common) reads on the wire as 3.14 one
    snapshot and -3.14 the next, and a naive lerp spins it the long way round
    through a full turn.  Exported for the harness. */
export function shortestArc(a, b) {
  let d = (b - a) % (2 * Math.PI);
  if (d > Math.PI) d -= 2 * Math.PI;
  else if (d < -Math.PI) d += 2 * Math.PI;
  return d;
}

const lerp = (a, b, f) => a + (b - a) * f;

export class Client {
  constructor() {
    this.ws = null;
    this.connected = false;
    /** True once this client has decided the network does not exist for this
        session: file:// pages, or a /ws nobody ever answered.  The game
        treats it exactly like "not connected yet", forever, silently. */
    this.offline = false;
    /** Set on CLOSE.REPLACED -- the same cat joined from another tab.  The
        other tab is the player; this one must not fight it by reconnecting. */
    this.replaced = false;
    /** Set on CLOSE.PROTO -- this build's dialect fell outside the server's
        version window (ARCH-PLAN §4.3).  The server's answer cannot change
        until one of us is updated, so reconnecting is knocking on a door
        that already said why: the facade owns the per-platform reaction
        (net/index.js), this flag only stops the retry loop under it. */
    this.outdated = false;
    /** Latest app-level round trip, ms, or -1 before the first pong. */
    this.rtt = -1;

    this._handlers = new Map();          // tag -> Set of fns
    this._closedByUs = false;
    this._attempts = 0;                  // failures since the last open
    this._everConnected = false;
    this._reconnectTimer = 0;
    this._pingTimer = 0;

    // --- the snapshot buffer ---
    /** Oldest first: { time (server seconds), rows: Map(slot -> row) }. */
    this.snaps = [];
    this.interp = new InterpDelay();
    /** The clock remote cats are drawn at -- always in the past by
        interp.value.  0 means "not started"; server time is a tick counter
        well above zero from the first snapshot. */
    this.renderTime = 0;

    this._lastPoseAt = 0;
    this._lastPoseFrame = null;
    /** Default sample target, for callers that want a reading, not a slot in
        a per-entry cache (main.js reads its own row once a frame).  Reused
        across calls -- read it before sampling again. */
    this._sample = { x: 0, y: 0, z: 0, facing: 0, state: STATES[0], charge: 0, happy: 0, flags: 0, speed: 0 };
  }

  /** Subscribe to one message tag (or OPENED/CLOSED).  Returns unsubscribe. */
  on(type, fn) {
    let set = this._handlers.get(type);
    if (!set) this._handlers.set(type, (set = new Set()));
    set.add(fn);
    return () => set.delete(fn);
  }

  _emit(type, msg) {
    const set = this._handlers.get(type);
    if (!set) return;
    for (const fn of set) {
      try {
        fn(msg);
      } catch (err) {
        // One broken subscriber must not take the socket -- and therefore
        // the render loop it shares a thread with -- down with it.
        console.error(`[net] handler for "${type}" threw`, err);
      }
    }
  }

  /**
   * Open the socket, or decide once that there is nothing to open.
   *
   * The URL is DERIVED, never configured: same host as the page, scheme
   * matched to the page's own (an https page with a ws: socket is refused as
   * mixed content before a single byte moves, and a hand-set URL is exactly
   * how that ships).  Which also settles the offline story for free -- the
   * artifact build runs off file://, where there is no host to derive, so
   * the whole network layer stands down before it can put one error in the
   * console.
   */
  connect() {
    if (this.ws || this.offline || this.replaced || this.outdated || this._closedByUs) return;
    if (
      typeof WebSocket === "undefined" ||
      typeof location === "undefined" ||
      (location.protocol !== "http:" && location.protocol !== "https:") ||
      !location.host
    ) {
      this.offline = true;
      return;
    }

    const scheme = location.protocol === "https:" ? "wss:" : "ws:";
    let ws;
    try {
      ws = new WebSocket(`${scheme}//${location.host}/ws`);
    } catch {
      // A URL the constructor itself refuses will refuse it next time too.
      this.offline = true;
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.connected = true;
      this._everConnected = true;
      this._attempts = 0;
      // A fresh socket knows nothing about us: the change-suppression below
      // must not "save" the first pose because the OLD connection sent one
      // like it.
      this._lastPoseFrame = null;
      this._pingTimer = setInterval(() => {
        this.send({ t: "ping", c: performance.now() });
      }, PING_MS);
      this._emit(OPENED, undefined);
    };

    // onerror carries no information a close does not; onclose always
    // follows, so all teardown lives there.
    ws.onerror = () => {};

    ws.onclose = (ev) => {
      if (this.ws !== ws) return;        // a stale socket's late close event
      this.ws = null;
      this.connected = false;
      this.rtt = -1;
      if (this._pingTimer) {
        clearInterval(this._pingTimer);
        this._pingTimer = 0;
      }
      if (ev && ev.code === CLOSE.REPLACED) {
        // The same token joined from another tab.  Reconnecting from here
        // would bounce the cat between the two sockets forever -- whichever
        // tab the player is actually in, the other one must concede.
        this.replaced = true;
      }
      if (ev && ev.code === CLOSE.PROTO) {
        // The server refused this build's protocol version outright.  A
        // retry would be denied identically forever; see the field's note.
        this.outdated = true;
      }
      this._emit(CLOSED, { code: ev ? ev.code : 0 });
      this._scheduleReconnect();
    };

    ws.onmessage = (ev) => {
      const msg = parseServerMessage(ev.data);
      if (!msg) return;                  // truncated, injected, or not JSON: drop
      if (msg.t === "snap") this._ingestSnap(msg);
      else if (msg.t === "pong" && typeof msg.c === "number" && Number.isFinite(msg.c)) {
        this.rtt = Math.max(0, performance.now() - msg.c);
      }
      // A tag nobody subscribed to -- including one this build has never
      // heard of -- falls straight through _emit and is silently ignored.
      // A newer server must degrade an old tab, not detonate it.
      this._emit(msg.t, msg);
    };
  }

  _scheduleReconnect() {
    if (this._closedByUs || this.replaced || this.outdated || this.offline || this._reconnectTimer) return;
    this._attempts++;
    if (!this._everConnected && this._attempts >= NEVER_CONNECTED_TRIES) {
      // Nobody has ever answered: this deployment has no server.  Stand
      // down for the life of the tab; the game plays single-player.
      this.offline = true;
      // And SAY so.  Standing down silently was the whole of a bug: the
      // last thing the layer above heard was the third close, which still
      // read `offline: false`, so a deployment with a host but no /ws --
      // the artifact build on https, or the game served off any static
      // bucket -- left a new player's join card up for the life of the tab
      // with nothing behind it to answer.  file:// never showed it, because
      // there connect() decides offline synchronously before the card is
      // ever asked for; every other serverless host did.
      this._emit(CLOSED, { code: 0, offline: true });
      return;
    }
    const base = Math.min(
      RECONNECT_MAX_MS,
      RECONNECT_MIN_MS * Math.pow(2, this._attempts - 1),
    );
    const wait = base * (0.7 + Math.random() * 0.6);
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = 0;
      this.connect();
    }, wait);
  }

  /** Deliberate local close: no reconnect. */
  close() {
    this._closedByUs = true;
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = 0;
    }
    this.ws?.close();
  }

  send(obj) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(obj));
    }
  }

  /**
   * Report the local cat's pose, at most once per STATE_SEND_MS, and only
   * when the WIRE would notice.  Called every frame; the gates make that
   * cheap.
   *
   * The change test compares the rounded frames, not the raw floats: the
   * wire rounds positions to the centimetre and facing to ~0.06 degrees
   * (protocol.js), so a float that moved less than that produces a frame
   * byte-identical to the last one, and sending it tells the server nothing
   * it does not already know.  A cat asleep on the porch therefore costs
   * zero messages -- the server holds its last pose, exactly as it would
   * have re-derived it from a duplicate.  Sub-rounding drift never
   * accumulates into a visible error, because each frame is rounded from the
   * ABSOLUTE position, not from a delta.
   */
  sendPose(player, state) {
    if (!this.connected) return;
    const now = performance.now();
    if (now - this._lastPoseAt < STATE_SEND_MS) return;
    const frame = poseFrame({
      x: player.pos.x, y: player.pos.y, z: player.pos.z,
      facing: player.facing,
      state,
      charge: player.charge,
    });
    const last = this._lastPoseFrame;
    if (last) {
      let same = true;
      for (let i = 0; i < frame.length; i++) {
        if (frame[i] !== last[i]) { same = false; break; }
      }
      if (same) return;                  // rate clock deliberately NOT reset:
                                         // the next real change goes out at
                                         // once, not up to 50 ms late
    }
    this._lastPoseAt = now;
    this._lastPoseFrame = frame;
    this.send({ t: "state", s: frame });
  }

  // --- snapshots -------------------------------------------------------------

  /**
   * One snapshot off the wire and into the buffer.  Every row is checked the
   * way readPose checks a pose -- a NaN written into a Map here is a NaN in
   * every distance and lerp that touches that slot until the tab dies.
   */
  _ingestSnap(msg) {
    const time = msg.time;
    if (typeof time !== "number" || !Number.isFinite(time)) return;
    if (!Array.isArray(msg.c)) return;

    const newest = this.snaps[this.snaps.length - 1];
    if (newest) {
      if (time === newest.time) return;  // duplicate stamp: nothing to add
      if (time < newest.time) {
        // Server time went backwards: a restart, not reordering (TCP does
        // not reorder).  The old buffer is a different timeline -- drop it
        // whole and let the clock re-seat on the new one.
        this.snaps.length = 0;
        this.renderTime = 0;
      }
    }

    const rows = new Map();
    for (const row of msg.c) {
      if (!Array.isArray(row) || row.length < CAT_ROW_LEN) continue;
      let ok = true;
      for (let i = 0; i < CAT_ROW_LEN; i++) {
        if (typeof row[i] !== "number" || !Number.isFinite(row[i])) { ok = false; break; }
      }
      if (!ok) continue;
      const slot = row[CatCol.SLOT] | 0;
      if (slot < 0 || slot >= MAX_CATS) continue;
      rows.set(slot, row);
    }

    this.interp.noteArrival(performance.now(), time * 1000);
    this.snaps.push({ time, rows });
    while (
      this.snaps.length > SNAP_MAX ||
      (this.snaps.length > 2 && time - this.snaps[0].time > SNAP_KEEP_S)
    ) {
      this.snaps.shift();
    }
    // A plain array with shift, not a real ring: sixty entries, one shift
    // per arrival, and the bracket search below wants ordinary indexing.
  }

  /**
   * Advance the render clock toward (newest - adaptive delay).  Once per
   * frame, at the top, before anything samples -- interp.update measures how
   * long a snapshot waited after a frame STARTED, and calling this late in
   * the frame would report a thread that is never busy.
   */
  update(dt) {
    this.interp.update(dt, performance.now());
    const newest = this.snaps[this.snaps.length - 1];
    if (this.snaps.length < 2 || !newest) return;
    const target = newest.time - this.interp.value;
    if (this.renderTime === 0) {
      this.renderTime = target;
      return;
    }
    this.renderTime += dt;
    const err = target - this.renderTime;
    if (Math.abs(err) > RENDER_SNAP_S) this.renderTime = target;
    else this.renderTime += err * (1 - Math.exp(-dt / RENDER_EASE_TAU));
  }

  /**
   * The two snapshots straddling `time`, plus the blend factor.  Clamps at
   * both ends (a === b) rather than returning null: off the back of the
   * buffer the pose freezes at the oldest thing known, off the FRONT it
   * holds the newest.  Holding is the whole policy on extrapolation -- there
   * is none.  A guessed cat walks through a fence it actually stopped at and
   * then snaps back when the truth arrives; a held cat merely pauses, which
   * for an animal that spends half its life motionless is barely a tell.
   */
  _bracketAt(time) {
    const s = this.snaps;
    if (!s.length) return null;
    let i = s.length - 1;
    while (i > 0 && s[i].time > time) i--;
    const a = s[i];
    const b = s[i + 1] ?? a;
    const span = b.time - a.time;
    const f = span > 1e-4 ? Math.max(0, Math.min(1, (time - a.time) / span)) : 0;
    return { a, b, f };
  }

  /**
   * One slot's pose at `time`, written into the caller's object -- fifteen
   * cats at display rate must not allocate.  Returns null only when the
   * buffer holds nothing for the slot at all; the roster then simply leaves
   * that cat where it last stood.
   *
   * Dropped snapshots need no special case here: the bracket happily spans
   * 100 ms instead of 50 and the lerp runs across it, which is precisely the
   * smooth line through the gap.  (Position was rounded to the centimetre on
   * the wire, so the worst kink a drop can add is the rounding itself.)
   */
  sampleAt(slot, time, out = this._sample) {
    const br = this._bracketAt(time);
    if (!br) return null;
    const rowA = br.a.rows.get(slot);
    const rowB = br.b.rows.get(slot);
    let a = rowA, b = rowB, f = br.f;
    if (!a || !b) {
      // Present at only one end: the slot appeared or vanished between the
      // two.  Hold the end that knows it -- blending toward a row that does
      // not exist has no meaning.
      a = b = rowA || rowB;
      f = 0;
      if (!a) return null;
    }

    out.x = lerp(a[CatCol.X], b[CatCol.X], f);
    out.y = lerp(a[CatCol.Y], b[CatCol.Y], f);
    out.z = lerp(a[CatCol.Z], b[CatCol.Z], f);
    out.facing = a[CatCol.FACING] + shortestArc(a[CatCol.FACING], b[CatCol.FACING]) * f;
    out.charge = lerp(a[CatCol.CHARGE], b[CatCol.CHARGE], f);
    out.happy = lerp(a[CatCol.HAPPY], b[CatCol.HAPPY], f);

    // Discrete columns take the NEARER end, never a blend and never always
    // the older one: a sample renders a known moment, and "which snapshot is
    // this moment closest to" is the honest answer for a value that cannot
    // be interpolated.  (Blending a state INDEX would invent states; always
    // taking `a` would hold every transition back half a snapshot.)
    const s = f >= 0.5 ? b : a;
    out.state = STATES[s[CatCol.STATE]] || STATES[0];
    out.flags = s[CatCol.FLAGS] | 0;

    // Ground speed across the whole bracket, for rateFor.  The full 50 ms
    // step, not an instantaneous difference: positions carry centimetre
    // rounding, and 1 cm over one 50 ms step is already 0.2 m/s of invented
    // speed -- the roster smooths on top, but the wider the baseline here
    // the less there is to smooth.
    const span = br.b.time - br.a.time;
    if (rowA && rowB && rowA !== rowB && span > 1e-4) {
      out.speed = Math.hypot(
        rowB[CatCol.X] - rowA[CatCol.X],
        rowB[CatCol.Z] - rowA[CatCol.Z],
      ) / span;
    } else {
      out.speed = 0;
    }
    return out;
  }

  /** Sample at the live render clock -- what every remote cat draws. */
  sampleLive(slot, out = this._sample) {
    return this.renderTime > 0 ? this.sampleAt(slot, this.renderTime, out) : null;
  }
}
